"use client"

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Input } from './ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select'
import { Search, Users, User, Tag, Plus, Edit, Trash2, Image as ImageIcon } from 'lucide-react'
import { api } from '../lib/api-client'
import { useToast } from '../hooks/use-toast'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog'
import { sortPlayersByJerseyAndName } from '../utils/playerSorting'

export default function PlayersTab({ setActiveTab, setSelectedPlayerId }) {
  const { toast } = useToast()
  const [players, setPlayers] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [genderFilter, setGenderFilter] = useState('all')
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false)
  const [showEditPlayerDialog, setShowEditPlayerDialog] = useState(false)
  const [currentPlayer, setCurrentPlayer] = useState(null)
  const [newPlayerData, setNewPlayerData] = useState({
    name: '',
    team: 'men',
    position: '',
    jerseyNumber: ''
  })

  const [positions] = useState(['Goalkeeper', 'Defender', 'Midfielder', 'Forward'])

  // Fetch players from API
  const fetchPlayers = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.getPlayers({ 
        searchTerm, 
        genderFilter 
      })

      if (response.success) {
        setPlayers(response.data || [])
      } else {
        setError(response.message || 'Failed to fetch players')
        toast({
          variant: 'destructive',
          title: 'Error fetching players',
          description: response.message
        })
      }
    } catch (error) {
      console.error('Error fetching players:', error)
      setError(error.message || 'Failed to fetch players')
      toast({
        variant: 'destructive',
        title: 'Error fetching players',
        description: error.message
      })
    } finally {
      setIsLoading(false)
    }
  }, [searchTerm, genderFilter, toast])

  // Fetch players when component mounts
  useEffect(() => {
    fetchPlayers()
  }, [fetchPlayers])

  // Handle search with automatic filtering
  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
    // Trigger automatic search after a short delay
    setTimeout(() => {
      fetchPlayers()
    }, 300)
  }

  // Handle gender filter change with automatic filtering
  const handleGenderFilterChange = (value) => {
    setGenderFilter(value)
    // Trigger automatic filtering
    setTimeout(() => {
      fetchPlayers()
    }, 100)
  }



  // Reset filters
  const resetFilters = () => {
    setSearchTerm('')
    setGenderFilter('all')
    fetchPlayers()
  }

  // Open add player dialog
  const openAddPlayerDialog = () => {
    setNewPlayerData({
      name: '',
      team: 'men',
      position: '',
      jerseyNumber: ''
    })
    setShowAddPlayerDialog(true)
  }

  // Handle new player data change
  const handleNewPlayerDataChange = (field, value) => {
    setNewPlayerData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Create new player
  const createPlayer = async () => {
    try {
      const response = await api.createPlayer(newPlayerData)
      
      if (response.success) {
        toast({
          title: 'Player created',
          description: 'Player has been created successfully'
        })
        setShowAddPlayerDialog(false)
        fetchPlayers()
      } else {
        toast({
          variant: 'destructive',
          title: 'Error creating player',
          description: response.message
        })
      }
    } catch (error) {
      console.error('Error creating player:', error)
      toast({
        variant: 'destructive',
        title: 'Error creating player',
        description: error.message
      })
    }
  }

  // Open edit player dialog
  const openEditPlayerDialog = (player) => {
    setCurrentPlayer(player)
    setNewPlayerData({
      name: player.name,
      team: player.team,
      position: player.position,
      jerseyNumber: player.jerseyNumber
    })
    setShowEditPlayerDialog(true)
  }

  // Update player
  const updatePlayer = async () => {
    try {
      const response = await api.updatePlayer(currentPlayer.id, newPlayerData)
      
      if (response.success) {
        toast({
          title: 'Player updated',
          description: 'Player has been updated successfully'
        })
        setShowEditPlayerDialog(false)
        fetchPlayers()
      } else {
        toast({
          variant: 'destructive',
          title: 'Error updating player',
          description: response.message
        })
      }
    } catch (error) {
      console.error('Error updating player:', error)
      toast({
        variant: 'destructive',
        title: 'Error updating player',
        description: error.message
      })
    }
  }

  // Delete player
  const deletePlayer = async (playerId) => {
    if (!window.confirm('Are you sure you want to delete this player?')) {
      return
    }

    try {
      const response = await api.deletePlayer(playerId)
      
      if (response.success) {
        toast({
          title: 'Player deleted',
          description: 'Player has been deleted successfully'
        })
        fetchPlayers()
      } else {
        toast({
          variant: 'destructive',
          title: 'Error deleting player',
          description: response.message
        })
      }
    } catch (error) {
      console.error('Error deleting player:', error)
      toast({
        variant: 'destructive',
        title: 'Error deleting player',
        description: error.message
      })
    }
  }

  // View player photos
  const viewPlayerPhotos = (player) => {
    try {
      // Set the selected player ID in the parent component
      if (setSelectedPlayerId) {
        setSelectedPlayerId(player.id);
      }
      
      // Navigate to the library tab
      if (setActiveTab) {
        setActiveTab('library');
      }
    } catch (error) {
      console.error('Error navigating to library tab:', error);
      // Fallback to showing the alert
      alert(`Viewing photos for ${player.name}. Navigate to the Library tab to see images containing this player.`);
    }
  }

  // Memoized sorted players to prevent sorting on every render
  const sortedPlayers = useMemo(() => {
    return sortPlayersByJerseyAndName(players);
  }, [players]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold">Players</h2>
        <Button onClick={openAddPlayerDialog}>
          <Plus className="mr-2 h-4 w-4" /> Add Player
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search players..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
            </div>
            <div className="w-[200px]">
              <Select value={genderFilter} onValueChange={handleGenderFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by gender" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem key="all-genders" value="all">All Players</SelectItem>
                  <SelectItem key="men" value="men">Men</SelectItem>
                  <SelectItem key="women" value="women">Women</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={resetFilters}>Reset</Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex justify-center my-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 p-4 rounded-md text-center my-6">
          <p className="text-destructive font-medium">{error}</p>
          <Button variant="outline" className="mt-2" onClick={fetchPlayers}>Retry</Button>
        </div>
      ) : players.length === 0 ? (
        <div className="text-center p-12 border rounded-md">
          <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No players found</h3>
          <p className="text-muted-foreground mb-4">There are no players matching your criteria.</p>
          <Button onClick={openAddPlayerDialog}>Add Player</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedPlayers.map(player => (
            <Card key={player.id} className="overflow-hidden">
              <CardContent className="p-0">
                <div className="p-4">
                  <div className="flex justify-between">
                    <h3 className="text-lg font-semibold">{player.name}</h3>
                    <div className="flex space-x-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => viewPlayerPhotos(player)}
                        title="View photos containing this player"
                      >
                        <ImageIcon className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEditPlayerDialog(player)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deletePlayer(player.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center mt-2 text-sm text-muted-foreground">
                    <Tag className="h-4 w-4 mr-1" />
                    <span>Team: {player.team}</span>
                  </div>
                  <div className="flex items-center mt-1 text-sm text-muted-foreground">
                    <User className="h-4 w-4 mr-1" />
                    <span>Position: {player.position}</span>
                  </div>
                  {player.jerseyNumber && (
                    <div className="flex items-center mt-1 text-sm text-muted-foreground">
                      <span className="font-semibold mr-1">#</span>
                      <span>Jersey: {player.jerseyNumber}</span>
                    </div>
                  )}
                  <div className="flex items-center mt-1 text-sm text-muted-foreground">
                    <ImageIcon className="h-4 w-4 mr-1" />
                    <span>Photos: {player.imageCount || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add Player Dialog */}
      <Dialog open={showAddPlayerDialog} onOpenChange={setShowAddPlayerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Player</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="name">Name</label>
              <Input
                id="name"
                value={newPlayerData.name}
                onChange={(e) => handleNewPlayerDataChange('name', e.target.value)}
                placeholder="Player name"
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="team">Team</label>
              <Select
                value={newPlayerData.team}
                onValueChange={(value) => handleNewPlayerDataChange('team', value)}
              >
                <SelectTrigger id="team">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="men">Shamrock Rovers Men</SelectItem>
                  <SelectItem value="women">Shamrock Rovers Women</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label htmlFor="position">Position</label>
              <Select
                value={newPlayerData.position}
                onValueChange={(value) => handleNewPlayerDataChange('position', value)}
              >
                <SelectTrigger id="position">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map(position => (
                    <SelectItem key={position} value={position}>{position}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label htmlFor="jerseyNumber">Jersey Number</label>
              <Input
                id="jerseyNumber"
                value={newPlayerData.jerseyNumber}
                onChange={(e) => handleNewPlayerDataChange('jerseyNumber', e.target.value)}
                placeholder="Jersey number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddPlayerDialog(false)}>Cancel</Button>
            <Button onClick={createPlayer}>Create Player</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Player Dialog */}
      <Dialog open={showEditPlayerDialog} onOpenChange={setShowEditPlayerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Player</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="edit-name">Name</label>
              <Input
                id="edit-name"
                value={newPlayerData.name}
                onChange={(e) => handleNewPlayerDataChange('name', e.target.value)}
                placeholder="Player name"
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="edit-team">Team</label>
              <Select
                value={newPlayerData.team}
                onValueChange={(value) => handleNewPlayerDataChange('team', value)}
              >
                <SelectTrigger id="edit-team">
                  <SelectValue placeholder="Select team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="men">Shamrock Rovers Men</SelectItem>
                  <SelectItem value="women">Shamrock Rovers Women</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label htmlFor="edit-position">Position</label>
              <Select
                value={newPlayerData.position}
                onValueChange={(value) => handleNewPlayerDataChange('position', value)}
              >
                <SelectTrigger id="edit-position">
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map(position => (
                    <SelectItem key={position} value={position}>{position}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <label htmlFor="edit-jerseyNumber">Jersey Number</label>
              <Input
                id="edit-jerseyNumber"
                value={newPlayerData.jerseyNumber}
                onChange={(e) => handleNewPlayerDataChange('jerseyNumber', e.target.value)}
                placeholder="Jersey number"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPlayerDialog(false)}>Cancel</Button>
            <Button onClick={updatePlayer}>Update Player</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
